import { useState } from "react";

const categories = ["All", "Learning", "Parenting", "Curriculum", "Events", "Tips"];

const posts = [
  {
    id: 1,
    category: "Learning",
    tag: "Featured",
    title: "The Importance of Play-Based Learning in Early Childhood",
    excerpt: "Play is the language of children. Discover how play-based learning at Seekers Bay helps develop cognitive, social, and emotional skills in young minds.",
    date: "March 12, 2025",
    readTime: "4 min read",
    emoji: "🎮",
    color: "from-teal-400 to-cyan-500",
    featured: true,
  },
  {
    id: 2,
    category: "Curriculum",
    tag: "Popular",
    title: "How Montessori + Multiple Intelligence Theory Works Together",
    excerpt: "Learn how Seekers Bay combines two powerful philosophies to create a truly unique curriculum that nurtures every child's individual strengths.",
    date: "Feb 28, 2025",
    readTime: "6 min read",
    emoji: "🧩",
    color: "from-violet-400 to-purple-500",
    featured: false,
  },
  {
    id: 3,
    category: "Parenting",
    tag: "New",
    title: "How to Prepare Your Child for Their First Day of School",
    excerpt: "First days can be daunting for both parents and children. Here are proven strategies from our experienced educators to ease the transition.",
    date: "Feb 14, 2025",
    readTime: "5 min read",
    emoji: "🎒",
    color: "from-orange-400 to-amber-500",
    featured: false,
  },
  {
    id: 4,
    category: "Tips",
    tag: "",
    title: "Benefits of Art, Music & Craft in Child Development",
    excerpt: "Creative activities aren't just fun — they're essential. Explore how art, music, and craft sessions at our preschool build critical thinking and self-expression.",
    date: "Jan 30, 2025",
    readTime: "4 min read",
    emoji: "🎨",
    color: "from-pink-400 to-rose-500",
    featured: false,
  },
  {
    id: 5,
    category: "Learning",
    tag: "",
    title: "What is Multiple Intelligence? A Parent's Guide",
    excerpt: "Howard Gardner's theory of Multiple Intelligences suggests 8 distinct ways children learn. Find out what this means for your child's education.",
    date: "Jan 18, 2025",
    readTime: "7 min read",
    emoji: "🧠",
    color: "from-indigo-400 to-blue-500",
    featured: false,
  },
  {
    id: 6,
    category: "Parenting",
    tag: "",
    title: "Gadget-Free Childhood: Why It Matters More Than Ever",
    excerpt: "At Seekers Bay, we maintain a gadget-free atmosphere. Discover the profound benefits of unplugged, active play for social and cognitive development.",
    date: "Jan 5, 2025",
    readTime: "5 min read",
    emoji: "📵",
    color: "from-green-400 to-emerald-500",
    featured: false,
  },
  {
    id: 7,
    category: "Events",
    tag: "New",
    title: "Annual Day 2024: A Celebration of Young Achievers",
    excerpt: "Our Annual Day was a spectacular showcase of talent, creativity, and joy. Relive the highlights and see how our little stars shone bright.",
    date: "Dec 20, 2024",
    readTime: "3 min read",
    emoji: "🎉",
    color: "from-yellow-400 to-orange-500",
    featured: false,
  },
  {
    id: 8,
    category: "Tips",
    tag: "",
    title: "5 Signs Your Child is Ready for Preschool",
    excerpt: "Wondering if your little one is ready to start preschool? Our educators share the key developmental milestones to look out for.",
    date: "Dec 10, 2024",
    readTime: "4 min read",
    emoji: "✅",
    color: "from-cyan-400 to-teal-500",
    featured: false,
  },
  {
    id: 9,
    category: "Curriculum",
    tag: "",
    title: "Understanding Bodily-Kinesthetic Intelligence in Your Child",
    excerpt: "Does your child love to move, dance, and touch everything? They might have strong bodily-kinesthetic intelligence. Here's how we nurture it.",
    date: "Nov 25, 2024",
    readTime: "5 min read",
    emoji: "🏃",
    color: "from-fuchsia-400 to-pink-500",
    featured: false,
  },
];

const tagColors = {
  Featured: "bg-orange-500 text-white",
  Popular: "bg-violet-600 text-white",
  New: "bg-teal-600 text-white",
};

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = posts.filter((p) => {
    const matchCat = activeCategory === "All" || p.category === activeCategory;
    const matchSearch =
      search === "" ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const featured = filtered.find((p) => p.featured);
  const rest = filtered.filter((p) => !p.featured || activeCategory !== "All" || search !== "");

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">

      {/* ── HERO ── */}
      <section className="relative bg-[rgb(122,49,125)] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-5 py-16 md:py-20 text-center">
          <span className="inline-block text-xs uppercase tracking-widest text-teal-300 border border-teal-600 rounded-full px-4 py-1 mb-4">
            ✦ Our Blog
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
            Insights for <span className="text-orange-400">Growing Minds</span>
          </h1>
          <p className="text-teal-200 text-sm md:text-base max-w-lg mx-auto mb-8">
            Tips, guides, and stories from Seekers Bay Preschool — helping parents and educators nurture every unique child.
          </p>
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="w-full bg-white/10 border border-white/20 backdrop-blur rounded-full px-5 py-3 text-white placeholder-teal-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 transition"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-teal-300">🔍</span>
          </div>
        </div>
        {/* Breadcrumb */}
        <div className="relative z-10 max-w-6xl mx-auto px-5 pb-5 flex gap-2 text-xs text-teal-400">
          <span>Home</span><span>/</span><span className="text-white font-semibold">Blog</span>
        </div>
      </section>

      {/* ── CATEGORY FILTER ── */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex gap-2 overflow-x-auto scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all
                ${activeCategory === cat
                  ? "bg-teal-700 text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700"}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-12">

        {/* ── FEATURED POST ── */}
        {featured && activeCategory === "All" && search === "" && (
          <div className="mb-12">
            <span className="text-xs uppercase tracking-widest text-orange-500 font-semibold block mb-4">Featured Post</span>
            <div className="rounded-3xl overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow md:flex">
              {/* Image */}
              <div className={`bg-gradient-to-br ${featured.color} md:w-96 h-56 md:h-auto flex items-center justify-center flex-shrink-0`}>
                <span className="text-8xl">{featured.emoji}</span>
              </div>
              {/* Content */}
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">Featured</span>
                  <span className="bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">{featured.category}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3 leading-snug">
                  {featured.title}
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{featured.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span>📅 {featured.date}</span>
                    <span>•</span>
                    <span>⏱ {featured.readTime}</span>
                  </div>
                  <button className="bg-teal-700 hover:bg-teal-800 text-white font-bold px-5 py-2.5 rounded-full text-xs transition-all shadow">
                    Read More →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── POSTS GRID ── */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <span className="text-5xl block mb-3">📭</span>
            <p className="text-lg font-semibold">No articles found.</p>
            <button onClick={() => { setSearch(""); setActiveCategory("All"); }} className="mt-4 text-teal-600 underline text-sm">
              Clear filters
            </button>
          </div>
        ) : (
          <>
            {(activeCategory !== "All" || search !== "") && (
              <p className="text-sm text-gray-500 mb-6">
                Showing <strong>{filtered.length}</strong> article{filtered.length !== 1 ? "s" : ""}
                {activeCategory !== "All" ? ` in "${activeCategory}"` : ""}
                {search ? ` for "${search}"` : ""}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeCategory === "All" && search === "" ? rest : filtered).map((post) => (
                <article
                  key={post.id}
                  className="rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300 group flex flex-col"
                >
                  {/* Card image */}
                  <div className={`bg-gradient-to-br ${post.color} h-48 flex items-center justify-center relative`}>
                    <span className="text-6xl group-hover:scale-110 transition-transform duration-300">{post.emoji}</span>
                    {post.tag && (
                      <span className={`absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full ${tagColors[post.tag] || "bg-gray-200 text-gray-700"}`}>
                        {post.tag}
                      </span>
                    )}
                    <span className="absolute top-3 right-3 bg-white/20 backdrop-blur text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>

                  {/* Card body */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-bold text-gray-900 text-base leading-snug mb-2 group-hover:text-teal-700 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4">{post.excerpt}</p>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-400 space-y-0.5">
                        <p>📅 {post.date}</p>
                        <p>⏱ {post.readTime}</p>
                      </div>
                      <button className="flex items-center gap-1.5 text-teal-700 hover:text-teal-900 font-bold text-sm transition-colors">
                        Read More
                        <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs">→</span>
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        {/* ── LOAD MORE ── */}
        {filtered.length > 0 && (
          <div className="text-center mt-12">
            <button className="bg-teal-700 hover:bg-teal-800 text-white font-bold px-10 py-3.5 rounded-full transition-all shadow-md hover:shadow-lg text-sm">
              Load More Articles
            </button>
          </div>
        )}
      </div>

      {/* ── NEWSLETTER BANNER ── */}
      <section className="bg-gradient-to-r from-teal-800 to-teal-900 text-white py-14 px-5 mt-10">
        <div className="max-w-2xl mx-auto text-center">
          <span className="text-4xl block mb-4">📬</span>
          <h2 className="text-2xl md:text-3xl font-extrabold mb-3">
            Stay Updated with Seekers Bay
          </h2>
          <p className="text-teal-200 text-sm mb-7">
            Get parenting tips, activity ideas, and school news delivered straight to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-white placeholder-teal-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 transition"
            />
            <button className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-3 rounded-full text-sm transition-all shadow flex-shrink-0">
              Subscribe →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}